import React, { useState, useCallback, useEffect, useRef, createRef } from "react";
import {
  Grid,
  GridColumn,
  GridNoRecords,
  getSelectedState,
  getSelectedStateFromKeyDown,
} from "@progress/kendo-react-grid";
import { getter } from "@progress/kendo-react-common";
import { Splitter } from "@progress/kendo-react-layout";
import { Popup } from "@progress/kendo-react-popup";
import { toast } from "react-toastify";
import styles from "../Casefile.module.scss";
import activityStyles from "./CaseActivity.module.scss";
import { formatDateForAPI } from "components/common/formatter.js";
import {
  getDocument,
  getDocumentEditor,
  getMatterDocDuplicate,
  getSingleMatterActivity,
  updateMatterActivity,
  saveDocumentEditor,
  downloadDocumentAsPDF,
  downloadDocument,
  downloadDocumentsAsZip,
  getOutlookWebURLViewEmail,
} from "api/index";

import CaseActivityForm from "./CaseActivityForm";
import DocumentViewer from "components/common/DocumentViewer";
import { fileIcon } from "components/common/fileIcon";
import { useDoubleClick } from "components/common/hooks/SingleDoubleClick";

import ShareDocumentModal from "../ShareDocumentModal";
import { HandleGridBody } from "components/common/HandleGridBody";
import FilterComponent from "components/CaseFiles/FilterComponent";
import DocumentEditor from "components/common/EditDocumentDialog";

import ConfirmationDialog from "components/common/ConfirmationDialog";
import ESigning from "../ESigning";
import MenuCell from "./MenuCell";
import { useCompany } from "services/companyService";
import { getActiveActivityTypes } from "services/activityTypeService";
import { GridLoader } from "components/GridLoader";
import ChitRequest from "pages/ChitRequest/ChitRequest";
import { GRID_PAGE_SIZES } from "components/common/GridComponents";
import { MdOutlineBlock } from "react-icons/md";
import { useDispatch } from "react-redux";
import { clearMattersBalances } from "actions/matters.action";
import {
  useCurrentUserSettings,
  updateCurrentUserSettings,
} from "services/userSettingsService";
import { useApplicationSettings } from "services/applicationSettingsService";

const DEFAULT_PANES = [
  {
    size: "100%",
    max: "70%",
    min: "450px",
    collapsible: true,
  },
  {},
];

const DATA_ITEM_KEY = "activityId";
const USER_SETTING_DOC_VIEWER_WIDTH_KEY = "CaseActivitiesDocViewerWidth";

const CaseActivitiesPage = (props) => {
  const idGetter = getter(DATA_ITEM_KEY);
  const dispatch = useDispatch();
  const { data: userSettings } = useCurrentUserSettings();
  const { data: applicationSettings } = useApplicationSettings();

  const getDocViewerWidth = () => {
    const savedWidth = userSettings?.find(
      (s) => s.key === USER_SETTING_DOC_VIEWER_WIDTH_KEY,
    )?.value;
    return savedWidth || "40%";
  };

  const getDynamicDocOpenPanes = () => [
    {
      collapsible: true,
    },
    {
      size: getDocViewerWidth(),
      min: "500px",
      collapsible: false,
    },
  ];

  const SELECTED_FIELD = "selected";
  const uploadRef = createRef();
  const [eventDataState, setEventDataState] = useState();
  const [filterFeeEarner, setFillterFeeEarner] = useState();
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);
  const [editDocumentDialog, setEditDocumentDialog] = useState();
  const [eSigning, setESigning] = useState();
  const [activityTypes, setActivityTypes] = useState([]);
  const [droppedFilesActivity, setDroppedFilesActivity] = useState([]);
  const [selectedState, setSelectedState] = useState({});
  const [paneDocument, setPaneDocument] = useState([]);
  const [panes, setPanes] = useState(DEFAULT_PANES);
  const [loader, setLoader] = useState(true);
  const [showShareDocument, setShowShareDocument] = useState(false);
  const [currentDoc, setCurrentDoc] = useState({});
  const [isRefreshed, setIsRefreshed] = useState(false);
  const [updateFilter, setUpdateFilter] = useState(false);
  const [typeID, setTypeID] = useState({ name: "ALL", value: "" });
  const [multiSel, setMultiSel] = useState(false);
  const [chitRequestModal, setChitRequestModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  let date = new Date();

  const [toDateSelector, setToDateSelector] = useState(date);
  const { data: company } = useCompany();
  const locale = company?.locale;

  let yearsStartDate = new Date(company.yearStartDate);

  const [fromDate, setFromDate] = useState(formatDateForAPI(yearsStartDate));

  const [toDate, setToDate] = useState(formatDateForAPI(date));
  const [fromDateSelector, setFromDateSelector] = useState(yearsStartDate);

  const [list, setLists] = useState({
    data: [],
    total: 20,
  });
  const [filterParameters, setFilterParmeters] = useState({
    postingStatus: "false",
  });

  const [valueFrom, setValueFrom] = useState(false);
  const [valueTo, setValueTo] = useState(false);
  const createState = (skip, take) => {
    let pagerSettings = {
      pageSizes: GRID_PAGE_SIZES,
    };
    return {
      pageSize: take,
      pageable: pagerSettings,
    };
  };

  const [state, setState] = useState(createState(0, 200));
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [isOverGrid, setIsOverGrid] = useState(false);
  const activityGridRef = useRef(null);
  const [viewActivity, setViewActivity] = useState();
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [isView, setView] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const [dataState, setDataState] = useState({
    take: 200,
    skip: 0,
    sort: [
      {
        field: "date",
        dir: "desc",
      },
    ],
  });
  const prevSearchValueRef = useRef(props.searchValue);

  const defaultEmailClient = applicationSettings?.find(
    (x) => x.key === "DefaultEmailClient",
  )?.value;

  const isEmailFile = (fileName = "") => {
    const ext = fileName.split(".").pop()?.toLowerCase();
    return ext === "eml" || ext === "msg";
  };

  useEffect(() => {
    if (
      (prevSearchValueRef.current && !props.searchValue) ||
      (props.searchValue && props.searchValue !== prevSearchValueRef.current)
    ) {
      setLoader(true);
      // Reset selection when search changes
      setSelectedState({});
      setCurrentDoc({});
      setPaneDocument([]);
      setPanes(DEFAULT_PANES);
    }
    prevSearchValueRef.current = props.searchValue;
  }, [props.searchValue]);

  const saveWidthTimeoutRef = useRef(null);

  const getActivityType = async () => {
    const data = await getActiveActivityTypes();
    setActivityTypes(data.slice());
  };

  useEffect(() => {
    getActivityType();
  }, []);

  useEffect(() => {
    if (props.refreshClicked && props.activeItem === 0) {
      handleRefresh();
      props?.setRefreshClicked(false);
    }
  }, [props?.refreshClicked]);

  const onOpen = () => {};

  useEffect(() => {
    if (!props?.filterClicked) return;

    const handlePointerDown = (event) => {
      const anchorElement = props?.filterAnchor?.current;
      if (anchorElement && anchorElement.contains(event.target)) {
        return;
      }
      if (event.target?.closest?.(".k-calendar")) {
        return;
      }

      if (event.target?.closest?.(".k-popup")) {
        return;
      }

      const popupElement = props?.filterContentRef?.current;
      const popupContainer = popupElement
        ? popupElement.closest(".k-animation-container")
        : null;

      const rect =
        popupContainer?.getBoundingClientRect?.() ||
        popupElement?.getBoundingClientRect?.();

      if (!rect) {
        props?.setFilterClicked(false);
        return;
      }

      const { clientX, clientY } = event;
      const isInside =
        clientX >= rect.left &&
        clientX <= rect.right &&
        clientY >= rect.top &&
        clientY <= rect.bottom;
      if (!isInside) {
        props?.setFilterClicked(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
    };
  }, [
    props?.filterClicked,
    props?.setFilterClicked,
    props?.filterAnchor,
    props?.filterContentRef,
  ]);

  useEffect(() => {
    return () => {
      clearTimeout(saveWidthTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (updateFilter) {
      setUpdateFilter(false);
    }
  }, [updateFilter]);

  const onChange = (event) => {
    if (event?.newState[0].collapsed) {
      setPanes([
        {
          ...event?.newState[0],
        },
        {},
      ]);
      return;
    } else if (!event?.newState[1].collapsed && !event?.newState[1].size) {
      setPanes(getDynamicDocOpenPanes());
      return;
    } else {
      setPanes([...event.newState]);
    }
    if (event.newState[1]?.size && paneDocument.length > 0) {
      clearTimeout(saveWidthTimeoutRef.current);
      saveWidthTimeoutRef.current = setTimeout(async () => {
        try {
          await updateCurrentUserSettings([
            {
              key: USER_SETTING_DOC_VIEWER_WIDTH_KEY,
              value: event.newState[1].size,
            },
          ]);
        } catch (error) {
          console.error("Failed to save document viewer width", error);
        }
      }, 500);
    }
  };

  const handleDrop = (event) => {
    if (!event || event.length === 0) {
      toast.error("No file found");
      return;
    }

    setDroppedFilesActivity([...event]);
    props.setAddActivity(true);
  };

  const onSelectionChange = (event) => {
    const newSelectedState = getSelectedState({
      event,
      selectedState: selectedState,
      dataItemKey: DATA_ITEM_KEY,
    });
    setMultiSel(true);
    setSelectedState(newSelectedState);
    showPaneFile(
      list?.data?.filter(
        (item) => item.activityId == Object.keys(newSelectedState)[0],
      )[0]?.documentId,
    );
  };

  const onKeyDown = (event) => {
    if (event.nativeEvent.code === "Enter") {
      const newSelectedState = getSelectedStateFromKeyDown({
        event,
        selectedState: selectedState,
        dataItemKey: DATA_ITEM_KEY,
      });
      setSelectedState(newSelectedState);
      showPaneFile(
        list?.data?.filter(
          (item) => item.activityId == Object.keys(newSelectedState)[0],
        )[0]?.documentId,
      );
    }
  };

  const handleTypeChange = (value) => {
    setTypeID({
      name: value.value?.name,
      value: value.value?.activityTypeID || value.value?.value,
    });
  };

  const onChangeFromDateSelect = (event) => {
    if (!valueFrom) {
      setValueFrom(true);
    }
    setFromDateSelector(event.value);
    let fromDate = null;
    let date = new Date(event.value);
    if (event.value) {
      fromDate = formatDateForAPI(date);
    }
    setFromDate(fromDate);
  };

  const onMatterRefClickActions = () => {};

  const onChangeToDateSelect = (event) => {
    if (!valueTo) {
      setValueTo(true);
    }
    setToDateSelector(event.value);
    let toDate = null;
    let date = new Date(event.value);
    if (event.value) {
      toDate = formatDateForAPI(date);
    }
    setToDate(toDate);
  };

  const updateFilterValues = () => {
    const { setFilterClicked, setFilterApplied } = props;
    setUpdateFilter(true);
    setFilterClicked(false);

    if (!valueFrom && !valueTo && typeID?.value === "") {
      setFromDateSelector(yearsStartDate);
      setToDateSelector(date);
      setFilterApplied(false);
      return;
    }
    setFilterApplied(true);
  };

  const deleteMatteractivity = async (data) => {
    try {
      await updateMatterActivity(props.matterId, data?.activityId, {
        ...data,
        active: false,
      });
      
      getActivitiesData();
      } catch (error) { }

      //Close confirmation dialog on error as well.
      setDeleteConfirmation();

  };

  const handleDownload = async (documentId, asPDF = false, fromDocView) => {
    setLoader(true);
    try {
      const selectedDocumentIds = Object.keys(selectedState)
        .filter((key) => selectedState[key])
        .map((key) => {
          const selectedItem = list?.data?.find((item) => item.activityId == key);
          return selectedItem?.documentId;
        })
        .filter((id) => id);

      let response;
      let fileName;
      if (selectedDocumentIds.length > 1 && !fromDocView) {
        fileName =
          (props?.matterDetails?.matterRef?.replace(/\//g, "-") ||
            "downloaded-folder") + ".zip";
        response = await downloadDocumentsAsZip(fileName, selectedDocumentIds);
      } else if (selectedDocumentIds.length === 1 || documentId) {
        response = asPDF
          ? await downloadDocumentAsPDF(documentId || selectedDocumentIds[0])
          : await downloadDocument(documentId || selectedDocumentIds[0]);

        const contentDisposition = response.headers["content-disposition"];

        if (response.data?.filename) {
          fileName = response.data.filename;
        } else if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(
            /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
          );
          if (fileNameMatch && fileNameMatch[1]) {
            fileName = fileNameMatch[1].replace(/['"]/g, "");
          }
        }
        if (!fileName) {
          fileName =
            (response.data?.fileID || "downloaded-document") + (asPDF ? ".pdf" : "");
        }
      } else {
        setLoader(false);
        return;
      }

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const tempLink = document.createElement("a");
      tempLink.href = url;
      tempLink.setAttribute("download", fileName);
      document.body.appendChild(tempLink);
      tempLink.click();
      document.body.removeChild(tempLink);
      window.URL.revokeObjectURL(url);
    } catch (error) {} finally {
      setLoader(false);
    }
  };

  const handleDuplicate = async (matterId, activityId) => {
    try {
      const { data } = await getMatterDocDuplicate(matterId, activityId);
      props.setAddActivity(true);
      setViewActivity(data?.data);
      setView(true);
      setIsDuplicate(true);
    } catch (error) {}
  };

  const getActivitiesData = async () => {
    setIsRefreshed(!isRefreshed);
  };

  const handleActivityRowClick = async (activity) => {
    const matterId = props?.matterId ?? activity?.matterId;
    const activityId = activity?.activityId;

    const fetchLatestActivity = async () => {
      if (!matterId || !activityId) return activity;
      try {
        const { data } = await getSingleMatterActivity(matterId, activityId);
        return data?.data ?? data ?? activity;
      } catch (error) {
        toast.error("Could not load the latest activity details.");
        return activity;
      }
    };

    // Check if this activity should open ChitRequest modal
    if (activity.systemType === "Accounts" && activity.subType === "ChitRequest") {
      const latestActivity = await fetchLatestActivity();
      setSelectedActivity(latestActivity);
      setChitRequestModal(true);
      return;
    }

    const latestActivity = await fetchLatestActivity();
    setSelectedState({
      [activity.activityId]: true,
    });
    props.setAddActivity(true);
    setViewActivity(latestActivity);
    setView(true);
  };

  const handleRefresh = () => {
    if (eventDataState) {
      setDataState({
        ...eventDataState?.event,
        skip: 0,
      });
    } else {
      setDataState({
        ...dataState,
        take: 50,
        skip: 0,
      });
    }
    setIsRefreshed(!isRefreshed);
    setUpdateFilter(true);
  };

  const handleReset = () => {
    setUpdateFilter(true);
    setValueFrom(false);
    setValueTo(false);
    setFromDateSelector(yearsStartDate);
    setToDateSelector(date);
    setTypeID({ name: "ALL", value: "" });
    props.setFilterApplied(false);
    props.setFilterClicked(false);
  };

  const showDocumentFile = async (documentId, documentName) => {
    if (isEmailFile(documentName) && defaultEmailClient === "OfficeWebMail") {
      try {
        const { data } = await getOutlookWebURLViewEmail(documentId);
        const url = data?.data?.outlookWebUrl;
        if (url) {
          window.open(
            url,
            "_blank",
            "noopener,noreferrer,width=800,height=800,left=100,top=100",
          );
          return;
        }
      } catch (err) {
        console.error("Failed to open Outlook Web email", err);
      }
    }
    const matterRef = props.addFormText?.split("-")[0]?.trim();
    const encodedId = btoa(
      JSON.stringify({
        id: documentId,
        matterRef,
        matterId: props?.matterId,
      }),
    );

    window.open(`/sa/document-viewer?id=${encodedId}`, "_blank");
  };

  const getDocumentEditorData = async (id, type) => {
    try {
      setLoader(true);
      const { data } = await getDocumentEditor(id, props.matterId);
      let editUrl = data?.data?.editURL;
      if (type === "Local") {
        let ext = "";
        if (paneDocument && paneDocument[0]) {
          const doc = paneDocument[0];
          if (doc.documentName) {
            ext = doc.documentName.split(".").pop().toLowerCase();
          }
        }
        if (["xls", "xlsx"].includes(ext)) {
          editUrl = `ms-excel:ofe|u|${editUrl}`;
        } else {
          editUrl = `ms-word:ofe|u|${editUrl}`;
        }
      }
      window.open(editUrl, "_blank");
      showPaneFile(id);
    } catch (error) {
      const statusCode = error?.response?.status;
      // 460 is already handled globally
      if (statusCode !== 460) {
      }
    } finally {
      setLoader(false);
    }
  };

  const showPaneFile = async (id) => {
    setLoaded(false);
    try {
      if (!id) {
        setPaneDocument([]);
        setPanes(getDynamicDocOpenPanes());
        return;
      }
      const { data } = await getDocument(id);
      setPaneDocument([data.data]);
      setPanes(getDynamicDocOpenPanes());
    } catch (error) {
      setPaneDocument([]);
    } finally {
      setLoaded(true);
    }
  };

  function truncateFileName(fileName) {
    if (fileName) {
      const extensionIndex = fileName.lastIndexOf(".");

      if (extensionIndex === -1) {
        return fileName.length > 8 ? fileName.substring(0, 18) : fileName;
      }

      const extension = fileName.substring(extensionIndex);

      if (fileName.length > 8) {
        return fileName.substring(0, 7 - extension.length) + extension;
      }

      return fileName;
    }
  }

  const DocumentCell = (e) => {
    const { documentId, documentName } = e.dataItem;

    const handleDocDragStart = (event) => {
      handleDragStart(event, e.dataItem);
    };

    const hybridClick = useDoubleClick(
      () => showDocumentFile(documentId, documentName),
      () => {
        setSelectedState({
          [e.dataItem.activityId]: true,
        });
        showPaneFile(documentId);
      },
    );

    return documentId ? (
      <td className={e.className ?? ""} style={e.style}>
        <div
          className="d-flex"
          draggable={true}
          onDragStart={handleDocDragStart}
          onClick={hybridClick}
          style={{ cursor: "grab" }}
          onMouseDown={(ev) => {
            ev.currentTarget.style.cursor = "grabbing";
          }}
          onMouseUp={(ev) => {
            ev.currentTarget.style.cursor = "grab";
          }}>
          {fileIcon(documentName)}

          <span className="ps-2 fs-sm d-lg-none d-xl-block">
            {truncateFileName(documentName)}
          </span>

          <span className="ps-2 fs-sm d-xl-none d-lg-block">
            {documentName?.split(".").pop()}
          </span>
        </div>
      </td>
    ) : (
      <td />
    );
  };

  const EmailCell = (props) => {
    const { emailFrom, emailTo, documentId, activityId } = props.dataItem;

    return (
      <td
        className={`fs-sm wrap-text ${props.className}`}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedState({
            [activityId]: true,
          });
          showPaneFile(documentId);
        }}>
        <div className="text-nowrap">{emailFrom}</div>
        <div className="text-nowrap">{emailTo}</div>
      </td>
    );
  };

  const saveDocuemntEdit = async (id) => {
    try {
      await saveDocumentEditor(id, props.matterId);
      toast.success("Document Saved");
      showPaneFile(id);
    } catch (error) {} finally {
      // setLoadingScreen(false);
    }
  };

  const DateCell = (props) => {
    const dateField = props.dataItem[props.field];

    if (dateField) {
      const dt = new Date(dateField);
      const dateStr = dt.toLocaleDateString(locale || "en-GB");
      const timeStr = dt.toLocaleTimeString(locale || "en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }); // e.g. 14:27

      return (
        <td
          onClick={(e) => {
            e.stopPropagation();
            setSelectedState({
              [props.dataItem.activityId]: true,
            });
            showPaneFile(props.dataItem?.documentId);
          }}
          className={`fs-sm ${props.className}`}>
          <div className="text-nowrap">{dateStr}</div>
          <div className="text-nowrap">{timeStr}</div>
        </td>
      );
    } else {
      return <td></td>;
    }
  };

  const dataStateChange = (e) => {
    const nextSort = e.dataState.sort;
    const prevSortField = dataState.sort?.[0]?.field ?? "date";

    if (
      nextSort !== undefined &&
      e.dataState.skip !== 0 &&
      dataState.sort !== nextSort
    ) {
      setDataState({ ...e.dataState, skip: 0 });
    } else if (nextSort && nextSort[0] === undefined) {
      setDataState({
        ...e.dataState,
        sort: [
          {
            field: prevSortField,
            dir: "asc",
          },
        ],
      });
    } else {
      setDataState(e.dataState);
    }
    setUpdateFilter(true);
  };

  const TypeCell = (prop) => {
    const handleTypeClick = (e) => {
      e.stopPropagation();
      handleActivityRowClick(prop.dataItem);
    };

    return (
      <td className="fs-sm wrap-text">
        <u style={{ cursor: "pointer" }} onClick={handleTypeClick}>
          {prop.dataItem.type}
        </u>
      </td>
    );
  };

  const dataReceived = (data) => {
    if (!data) return;
    setLists(data);
    setLoader(false);

    if (props.searchValue && data.data?.length > 0) {
      const firstRow = data.data[0];
      if (firstRow?.activityId) {
        setSelectedState({
          [firstRow.activityId]: true,
        });

        showPaneFile(firstRow.documentId ?? null);
      }
    }
  };

  const getRowStyle = (dataItem) => {
    const activityType = activityTypes.find(
      (type) => type.activityTypeID === dataItem.typeId,
    );

    if (
      activityType &&
      activityType.rowStyle &&
      typeof activityType.rowStyle === "string"
    ) {
      const match = activityType.rowStyle.match(/backgroundColor:['"]([^'"]+)['"]/);
      if (match && match[1]) {
        const backgroundColor = match[1];
        return { backgroundColor: backgroundColor };
      }
    }

    return {};
  };

  const handleDragStart = (e, dataItem) => {
    // Get all selected items
    const selectedItems = list?.data?.filter(
      (item) => selectedState[idGetter(item)],
    );

    // If the dragged row is not selected, use only that row
    const itemsToDrag =
      selectedItems.length > 0 && selectedState[idGetter(dataItem)]
        ? selectedItems
        : [dataItem];

    // Build payload in the format specified
    const selectedDocs = itemsToDrag
      .filter((item) => item.documentId) // Only include items with documents
      .map((item) => ({
        documentId: item.documentId,
        documentName: item.documentName || item.details,
        matterId: item.matterId || props.matterId,
        activityId: item.activityId,
        details: item.details,
      }));

    if (selectedDocs.length === 0) {
      e.preventDefault();
      return;
    }

    const payload = {
      documents: selectedDocs.map((d) => ({
        documentId: d.documentId,
        documentName: d.documentName,
        matterId: d.matterId,
        activityId: d.activityId,
      })),
    };

    const json = JSON.stringify(payload);

    e.dataTransfer.setData("text/plain", "LINQ_CASE_ACTIVITIES:" + json);
    e.dataTransfer.effectAllowed = "copy";
  };

  const mappedData = {
    data: list?.data?.map((item) => ({
      ...item,
      [SELECTED_FIELD]: selectedState[idGetter(item)],
    })),
    total: list?.total,
  };

  const hasFiles = useCallback((e) => {
    const types = e?.dataTransfer?.types;
    if (!types) return false;
    return typeof types.contains === "function"
      ? types.contains("Files")
      : Array.from(types).includes("Files");
  }, []);

  const onPageDragEnter = useCallback(
    (e) => {
      if (hasFiles(e)) setIsDraggingFile(true);
    },
    [hasFiles],
  );

  const onPageDragLeave = useCallback((e) => {
    const related = e.relatedTarget;
    if (related == null || !activityGridRef.current?.contains(related)) {
      setIsDraggingFile(false);
      setIsOverGrid(false);
    }
  }, []);

  const onPageDragOver = useCallback(
    (e) => {
      if (hasFiles(e)) e.preventDefault();
    },
    [hasFiles],
  );

  const onPageDrop = useCallback(() => {
    setIsDraggingFile(false);
    setIsOverGrid(false);
  }, []);

  return (
    <div
      ref={activityGridRef}
      className="activity-grid h-100"
      onDragEnter={onPageDragEnter}
      onDragLeave={onPageDragLeave}
      onDragOver={onPageDragOver}
      onDrop={onPageDrop}>
      <Splitter
        panes={panes}
        onChange={onChange}
        onBarToggle={() => {
          if (panes[0].collapsed) {
            setPanes(getDynamicDocOpenPanes());
          } else {
            setPanes([
              {
                collapsed: true,
              },
              {},
            ]);
          }
        }}
        className={"workflow-splitter border-0 h-100"}>
        <FileDragDrop
          onFilesDrop={handleDrop}
          uploadRef={uploadRef}
          onOverGridChange={setIsOverGrid}
          onDragEnd={() => {
            setIsDraggingFile(false);
            setIsOverGrid(false);
          }}
          isOverGrid={isOverGrid}
          isDraggingFile={isDraggingFile}>
          <Grid
            {...dataState}
            data={mappedData}
            sortable={true}
            dataItemKey={DATA_ITEM_KEY}
            selectedField={SELECTED_FIELD}
            selectable={{
              enabled: true,
              drag: false,
              cell: false,
              mode: "multiple",
            }}
            onDataStateChange={dataStateChange}
            onSelectionChange={onSelectionChange}
            onKeyDown={onKeyDown}
            className={`h-100 overflow-auto grid-row-select casefile_outlook_grid ${activityStyles["details-min-width"]}`}
            pageable={state.pageable}
            pageSize={state.pageSize}
            rowRender={(row, props) => {
              const style = getRowStyle(props.dataItem);
              return React.cloneElement(row, {
                style: {
                  ...row.props.style,
                  ...style,
                },
              });
            }}>
            <GridNoRecords>
              {loader ? (
                <div style={{ height: "100px" }}></div>
              ) : (
                mappedData.data &&
                mappedData.data.length === 0 &&
                "No records available"
              )}
            </GridNoRecords>
            <GridColumn
              field={SELECTED_FIELD}
              width="50px"
              headerCell={() => <></>}
            />

            <GridColumn
              field="type"
              className="fs-sm"
              title="Type"
              cell={TypeCell}
              width="120px"
            />
            <GridColumn
              field="date"
              title="Date"
              cell={DateCell}
              width="100px"
              className="p-1 cursor-default"
            />
            <GridColumn
              field="feeEarnerRef"
              title="FE"
              width="60px"
              className="cursor-default"
            />
            <GridColumn
              field="details"
              title="Details"
              className="text-wrap cursor-default"
            />
            <GridColumn
              field=""
              title=" "
              cell={(prop) => (
                <MenuCell
                  {...prop}
                  loaded={loaded}
                  setLoaded={setLoaded}
                  setSelectedState={setSelectedState}
                  selectedState={selectedState}
                  showPaneFile={showPaneFile}
                  multiSel={multiSel}
                  setMultiSel={setMultiSel}
                  setAddActivity={props.setAddActivity}
                  setViewActivity={setViewActivity}
                  setView={setView}
                  handleActivityRowClick={handleActivityRowClick}
                  showDocumentFile={showDocumentFile}
                  handleDownload={handleDownload}
                  getDocumentEditorData={getDocumentEditorData}
                  handleDuplicate={handleDuplicate}
                  setShowShareDocument={setShowShareDocument}
                  setCurrentDoc={setCurrentDoc}
                  setDeleteConfirmation={setDeleteConfirmation}
                  setESigning={setESigning}
                  matterId={props?.matterId}
                />
              )}
              width={"40px"}
            />
            <GridColumn
              title="Document"
              cell={DocumentCell}
              width="110px"
              className="fs-sm px-1"
            />
            <GridColumn
              title="Email"
              cell={EmailCell}
              width="200px"
              className="fs-sm px-1 cursor-default"
            />
          </Grid>
          {loader && <GridLoader />}
        </FileDragDrop>

        <div className="h-100">
          <DocumentViewer
            loader={loaded}
            matterId={props?.matterId}
            matter={props?.matterDetails}
            onDownload={handleDownload}
            saveDocuemntEdit={saveDocuemntEdit}
            data={paneDocument && paneDocument.length > 0 ? paneDocument : []}
            onRefresh={(id) => showPaneFile(id)}
            onScreenComponent={true}
            onEditDoc={(e, type) => getDocumentEditorData(e, type)}
            onClose={() => {
              setPanes(DEFAULT_PANES);
              setPaneDocument([]);
              setSelectedState([]);
            }}
            onExpand={() => {
              if (panes[0].collapsed) {
                setPanes(getDynamicDocOpenPanes());
              } else {
                setPanes([
                  {
                    collapsed: true,
                  },
                  {},
                ]);
              }
            }}
            onOpenInNewTab={showDocumentFile}
            component="caseActivities"
          />
        </div>
      </Splitter>

      {isDraggingFile && !isOverGrid && (
        <div
          className={activityStyles["drop-invalid-banner"]}
          role="status"
          aria-live="polite">
          <MdOutlineBlock
            className={activityStyles["drop-invalid-icon"]}
            aria-hidden
          />
          <span>Drop files only on the activity grid to attach to the case.</span>
        </div>
      )}

      <Popup
        key={props?.filterClicked ? "open" : "closed"}
        show={props?.filterClicked}
        anchor={props?.filterAnchor?.current}
        anchorAlign={{
          horizontal: "right",
          vertical: "bottom",
        }}
        popupAlign={{
          horizontal: "right",
          vertical: "top",
        }}
        popupClass={`${styles["popup-content"]}`}
        onOpen={onOpen}>
        <div
          ref={props?.filterContentRef}
          role="menu"
          className="d-flex flex-column align-items-center">
          <FilterComponent
            updateFilterValues={updateFilterValues}
            setUpdateFilter={setUpdateFilter}
            updateFilter={updateFilter}
            showPopup={true}
            valueFrom={valueFrom}
            setValueFrom={setValueFrom}
            fromDateSelector={fromDateSelector}
            onChangeFromDateSelect={onChangeFromDateSelect}
            valueTo={valueTo}
            setValueTo={setValueTo}
            toDateSelector={toDateSelector}
            onChangeToDateSelect={onChangeToDateSelect}
            typeData={[{ name: "ALL", value: "" }, ...activityTypes]}
            typeID={typeID}
            handleChange={handleTypeChange}
            handleReset={handleReset}
          />
        </div>
      </Popup>

      <HandleGridBody
        matterId={props.matterId}
        feeEarner={filterFeeEarner}
        typeId={typeID?.value}
        refresh={isRefreshed}
        updateFilter={updateFilter}
        fD={valueFrom ? fromDate : ""}
        tD={valueTo ? toDate : ""}
        dataState={dataState}
        onDataReceived={dataReceived}
        currentComponent="caseActivities"
        searchValue={props?.searchValue}
        showLoader={false}
      />

      {props.addActivity && (
        <CaseActivityForm
          getActivitiesData={getActivitiesData}
          addFormText={props.addFormText}
          droppedFilesActivity={droppedFilesActivity}
          viewActivity={viewActivity}
          feeEarnerRef={props.matterDetails?.feeEarnerRef}
          matterDetails={props.matterDetails}
          isView={isView}
          isDuplicate={isDuplicate}
          reference={props.matterId}
          onMatterRefClickActions={onMatterRefClickActions}
          setshowMatterForm={() => {
            setViewActivity();
            setView(false);
            setDroppedFilesActivity([]);
            if (isDuplicate) setIsDuplicate(false);
            props.setAddActivity(false);
          }}
          isAddActivityBtn={props.isAddActivityBtn}
        />
      )}

      {eSigning && (
        <ESigning
          matterId={props?.matterId}
          title={"Select Contacts"}
          activityId={eSigning}
          onClose={() => setESigning()}
        />
      )}

      {editDocumentDialog && (
        <DocumentEditor
          documentId={editDocumentDialog}
          cancelEdit={(isSucess) => {
            setEditDocumentDialog();
            if (isSucess) getActivitiesData();
          }}
          matterId={props.matterId}
          onDownload={handleDownload}
        />
      )}

      {showShareDocument && (
        <ShareDocumentModal
          onClose={() => setShowShareDocument(false)}
          selectedDocuments={Object.keys(selectedState)
            .filter((key) => selectedState[key])
            .map((key) => {
              const selectedItem = list?.data?.find(
                (item) => item.activityId == key,
              );
              return {
                id: selectedItem?.documentId,
                name: selectedItem?.documentName,
              };
            })
            .filter((item) => item.id && item.name)}
          matterId={props.matterId}
          matter={props.matterDetails}
          currentDoc={currentDoc}
        />
      )}

      {deleteConfirmation && (
        <ConfirmationDialog
          content={`Are you sure you want to delete activity '${deleteConfirmation?.details}'?`}
          onClickYes={() => {
            deleteMatteractivity(deleteConfirmation);
          }}
          onClickNo={() => setDeleteConfirmation()}
          onClose={() => setDeleteConfirmation()}
        />
      )}

      {chitRequestModal && (
        <ChitRequest
          cancelEdit={() => {
            setChitRequestModal(false);
            setSelectedActivity(null);
            dispatch(clearMattersBalances("Chit Request"));
          }}
          matter={props.matterDetails}
          component={"modal"}
          activityData={selectedActivity}
        />
      )}
    </div>
  );
};
export default CaseActivitiesPage;

export const FileDragDrop = ({
  children,
  onFilesDrop,
  onOverGridChange,
  onDragEnd,
  isDraggingFile,
  isOverGrid,
}) => {
  const [dragging, setDragging] = useState(false);
  const containerRef = React.useRef(null);

  const hasFilesTransfer = useCallback((e) => {
    const types = e?.dataTransfer?.types;
    if (!types) return false;
    return typeof types.contains === "function"
      ? types.contains("Files")
      : Array.from(types).includes("Files");
  }, []);

  const handleDragEnter = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (hasFilesTransfer(e)) {
        setDragging(true);
        onOverGridChange?.(true);
      }
    },
    [onOverGridChange, hasFilesTransfer],
  );

  const handleDragLeave = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!containerRef.current?.contains(e.relatedTarget)) {
        setDragging(false);
        onOverGridChange?.(false);
      }
    },
    [onOverGridChange],
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragging(false);
      onOverGridChange?.(false);
      onDragEnd?.();

      const data = e.dataTransfer.getData("text/plain");
      if (data?.startsWith("LINQ_CASE_ACTIVITIES:")) return;

      const files = e.dataTransfer.files;
      if (files?.length > 0) onFilesDrop(files);
    },
    [onFilesDrop, onOverGridChange, onDragEnd],
  );

  const showValidOverlay = dragging || (isDraggingFile && isOverGrid);

  return (
    <div
      ref={containerRef}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      className={`h-100 ${activityStyles["drop-zone"]}`}>
      {children}
      {showValidOverlay && (
        <div className={activityStyles["drop-overlay"]} aria-hidden>
          <span className={activityStyles["drop-overlay-title"]}>
            Drop files here to attach to the case activity
          </span>
          <span className={activityStyles["drop-overlay-hint"]}>
            Release to upload files to the case
          </span>
        </div>
      )}
    </div>
  );
};
